import type { MouseEvent, ReactNode } from 'react';

import {
  Box,
  ButtonBase,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import {
  type HealthTone,
  getHealthMainColor,
} from '@modules/organisations/OrganisationEngagementCard/healthTone';

const ROW_PY = 0.5;
const RING_SIZE = 18;
const RING_THICKNESS = 8;
const VALUE_WIDTH = 44;

/**
 * Color → tone mapping for callers that work with the legacy
 * "success | warning | error | disabled" palette (e.g. visibility scoring).
 */
export type SignalLegacyColor = 'success' | 'warning' | 'error' | 'disabled';

export const legacyColorToHealthTone = (
  color: SignalLegacyColor,
): HealthTone => {
  switch (color) {
    case 'success':
      return 'healthy';
    case 'warning':
      return 'fair';
    case 'error':
      return 'atRisk';
    case 'disabled':
    default:
      return 'neutral';
  }
};

export type ScoreSignalRowProps = {
  /** Left-aligned signal name. */
  label: string;
  /** Drives dot + bar color and (when ineligible) muted value styling. */
  tone: HealthTone;
  /** 0–100, clamped. Drives the bar fill. */
  percent: number;
  /** Right-aligned value, e.g. `"85%"` or `42`. */
  value: ReactNode;
  loading?: boolean;
  /** Hover tooltip; ignored when `onClick` is provided (use the popover instead). */
  tooltip?: string;
  /** Makes the row a clickable button (used by popover-based rows). */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /**
   * When `false`, the bar is hidden and the value is rendered in the disabled
   * text color (matches the engagement "ineligible signal" style). Defaults
   * to `true`.
   */
  eligible?: boolean;
};

/**
 * Single-line "signal" row used inside score cards (engagement, visibility,
 * etc.). Renders: ● dot · label · ── inline bar · value · (chevron).
 *
 * Keep this component visually identical across cards — any styling tweak
 * here should be intentional and global.
 */
export const ScoreSignalRow = ({
  label,
  tone,
  percent,
  value,
  loading = false,
  tooltip,
  onClick,
  eligible = true,
}: ScoreSignalRowProps) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: 1, py: ROW_PY, px: 1 }}
      >
        <Skeleton width="50%" sx={{ flex: 1 }} />
        <Skeleton width={VALUE_WIDTH} sx={{ flexShrink: 0 }} />
        <Skeleton
          variant="circular"
          width={RING_SIZE}
          height={RING_SIZE}
          sx={{ flexShrink: 0 }}
        />
      </Stack>
    );
  }

  const toneColor = getHealthMainColor(theme, tone);
  const clampedPercent = Math.min(Math.max(percent, 0), 100);

  const rowInner = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        noWrap
        sx={{ color: 'text.primary', fontWeight: 500, flex: 1, minWidth: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: eligible ? 700 : 400,
          color: eligible ? 'text.primary' : 'text.disabled',
          flex: `0 0 ${VALUE_WIDTH}px`,
          width: VALUE_WIDTH,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
      {eligible && (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            flexShrink: 0,
            width: RING_SIZE,
            height: RING_SIZE,
          }}
        >
          <CircularProgress
            variant="determinate"
            value={100}
            size={RING_SIZE}
            thickness={RING_THICKNESS}
            sx={{ color: 'action.hover', position: 'absolute', left: 0 }}
          />
          <CircularProgress
            variant="determinate"
            value={clampedPercent}
            size={RING_SIZE}
            thickness={RING_THICKNESS}
            sx={{
              color: toneColor,
              position: 'absolute',
              left: 0,
              '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
            }}
          />
        </Box>
      )}
    </Stack>
  );

  const rowWrapper =
    onClick != null ? (
      <ButtonBase
        component="div"
        onClick={onClick}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          borderRadius: 1,
          px: 1,
          py: ROW_PY,
          mx: -1,
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {rowInner}
      </ButtonBase>
    ) : (
      <Box sx={{ py: ROW_PY, px: 1, mx: -1, borderRadius: 1 }}>{rowInner}</Box>
    );

  if (tooltip != null && tooltip !== '' && onClick == null) {
    return (
      <Tooltip title={tooltip} placement="top" arrow>
        {rowWrapper}
      </Tooltip>
    );
  }

  return rowWrapper;
};
